import { useQuery } from "@tanstack/react-query";

import {
  CallWindowT,
  TopCallsFiltersI,
  TopCallsQueryI,
} from "@/api/routes/top-calls";
import { useApiClient } from "@/context/api-client";
import { rankedCallsQuery, topTenCallsQuery } from "../lib/phase4";
import { isManageSwitchedOff } from "@/lib/manage/errors";

// The unscoped options request also probes top_calls_enabled for the nav.
export const useTopCallsFilters = (workspaceId?: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ["top-calls", "filters", workspaceId ?? ""],
    queryFn: async (): Promise<TopCallsFiltersI | null> => {
      try {
        const { data } = await api.topCalls.getFilters(workspaceId);

        return data;
      } catch (error) {
        if (isManageSwitchedOff(error)) {
          return null;
        }

        throw error;
      }
    },
    enabled,
    retry: false,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useFetchTopCallsSwitch = () => {
  const query = useTopCallsFilters();

  return { ...query, data: !query.isError && !!query.data };
};

export const useTopCalls = (
  params: TopCallsQueryI,
  enabled: boolean,
  source: "all" | "my" | "top" = "all",
  window: CallWindowT = "30"
) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ["top-calls", "list", source, window, params],
    queryFn: async () => {
      if (source === "my") {
        return (await api.topCalls.getMyLive(params)).data;
      }
      if (source === "top") {
        return (await api.topCalls.getRanked(rankedCallsQuery(params, window)))
          .data;
      }
      return (await api.topCalls.getCalls(params)).data;
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useTopCallDetail = (id: string) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ["top-calls", "detail", id],
    queryFn: async () => (await api.topCalls.getCall(id)).data.call,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
};

export const useCallGate = (enabled: boolean) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ["top-calls", "gate"],
    queryFn: async () => (await api.topCalls.getGate()).data,
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
};
export const useCallTopTen = (
  params: TopCallsQueryI,
  window: CallWindowT,
  enabled: boolean
) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ["top-calls", "top10", topTenCallsQuery(params, window)],
    queryFn: async () =>
      (await api.topCalls.getTopTen(topTenCallsQuery(params, window))).data,
    enabled,
    retry: false,
  });
};
export const useMyCallsSummary = (enabled: boolean) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ["top-calls", "summary"],
    queryFn: async () => (await api.topCalls.getMySummary()).data,
    enabled,
    retry: false,
  });
};
export const useCallBreakdowns = (id: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ["top-calls", "breakdowns", id],
    queryFn: async () => (await api.topCalls.getBreakdowns(id)).data,
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
};

export const myBreakdownKey = (id: string) => ["top-calls", "my-breakdown", id];
export const useMyCallBreakdown = (id: string) => {
  const api = useApiClient();
  return useQuery({
    queryKey: myBreakdownKey(id),
    queryFn: async () => {
      try {
        return (await api.topCalls.getMyBreakdown(id)).data;
      } catch (error) {
        if (
          (error as { response?: { status?: number } })?.response?.status ===
          404
        ) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
};
